import React, { useCallback, useEffect, useRef, useState } from 'react';
import { getAppTitle, Tooltip } from 'venn-ui-kit';
import { cn } from '@/lib/utils';

interface PrintSettingProps {
  title: string;
  label: string;
  fontTooltip: string;
  settingContent: string;
  settingContentOnChange: (newValue?: string) => void;
  fontSize: number;
  fontSizeOnChange: (newSize?: number) => void;
  isReadOnly: boolean;
}

const ResetButton = ({ disabled, onClick }: { disabled?: boolean; onClick?: () => void }) => (
  <Tooltip content={`Reset to ${getAppTitle()} defaults`}>
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      className={cn(
        'text-[12px] leading-[100%] text-venn-dark-blue hover:text-venn-dark-blue rounded-[4px] p-1.5 transition-colors',
        'bg-venn-white hover:bg-venn-dark-blue/5 border border-solid border-venn-grey-30 group-focus-within:border-venn-dark-blue hover:border-venn-dark-blue',
      )}
    >
      Reset to {getAppTitle()} defaults
    </button>
  </Tooltip>
);

const toEditorHtml = (content: string | undefined): string => {
  if (!content) return '';
  if (/<[a-z][\s\S]*>/i.test(content)) return content;
  return content
    .split('\n\n')
    .filter((para) => para.trim())
    .map((para) => `<p>${para.replace(/\n/g, '<br>')}</p>`)
    .join('');
};

const saveEditorHtml = (el: HTMLDivElement): string => {
  const parts: string[] = [];
  el.childNodes.forEach((node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent ?? '';
      if (text.trim()) parts.push(`<p>${text}</p>`);
    } else if ((node as HTMLElement).tagName === 'DIV') {
      parts.push(`<p>${(node as HTMLElement).innerHTML}</p>`);
    } else {
      parts.push((node as HTMLElement).outerHTML);
    }
  });
  return parts.join('');
};

type FormatCommand = 'bold' | 'italic' | 'underline';

const FormatButton = ({
  command,
  label,
  icon,
  isActive,
  disabled,
  onFormat,
}: {
  command: FormatCommand;
  label: string;
  icon: string;
  isActive: boolean;
  disabled: boolean;
  onFormat: (command: FormatCommand) => void;
}) => (
  <Tooltip content={label}>
    <button
      type="button"
      aria-label={label}
      aria-pressed={isActive}
      disabled={disabled}
      onMouseDown={(e) => {
        e.preventDefault();
        onFormat(command);
      }}
      className={cn(
        'flex items-center justify-center w-6 h-6 rounded-[4px] border border-solid border-venn-grey-30 text-[12px]',
        'font-black p-1.5 leading-[100%] hover:bg-venn-dark-blue/5 transition-colors',
        'group-focus-within:text-venn-dark-blue group-focus-within:hover:text-venn-dark-blue group-focus-within:bg-venn-white',
        isActive ? 'bg-venn-dark-blue/15' : 'hover:border-venn-dark-blue hover:bg-venn-dark-blue/15',
        disabled && '!text-venn-grey-60 bg-transparent cursor-not-allowed pointer-events-none',
      )}
    >
      <i className={`fas fa-${icon}`} />
    </button>
  </Tooltip>
);

export const PrintSetting = ({
  title,
  label,
  settingContent,
  settingContentOnChange,
  isReadOnly,
}: PrintSettingProps) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const isFocusedRef = useRef(false);
  const [isFocused, setIsFocused] = useState(false);
  const [activeFormats, setActiveFormats] = useState({ bold: false, italic: false, underline: false });

  useEffect(() => {
    document.execCommand('defaultParagraphSeparator', false, 'p');
  }, []);

  useEffect(() => {
    if (editorRef.current && !isFocusedRef.current) {
      editorRef.current.innerHTML = toEditorHtml(settingContent);
    }
  }, [settingContent]);

  const updateActiveFormats = useCallback(() => {
    setActiveFormats({
      bold: document.queryCommandState('bold'),
      italic: document.queryCommandState('italic'),
      underline: document.queryCommandState('underline'),
    });
  }, []);

  const handleFormat = useCallback(
    (command: FormatCommand) => {
      editorRef.current?.focus();
      document.execCommand(command);
      updateActiveFormats();
      if (editorRef.current) {
        settingContentOnChange(saveEditorHtml(editorRef.current));
      }
    },
    [settingContentOnChange, updateActiveFormats],
  );

  const handleInput = useCallback(() => {
    if (editorRef.current) {
      settingContentOnChange(saveEditorHtml(editorRef.current));
    }
  }, [settingContentOnChange]);

  const handlePaste = useCallback((e: React.ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    document.execCommand('insertText', false, text);
  }, []);

  const handleKeyUp = useCallback(updateActiveFormats, [updateActiveFormats]);
  const handleMouseUp = useCallback(updateActiveFormats, [updateActiveFormats]);

  return (
    <div className="flex flex-col w-full">
      <div className="text-[14px] font-bold">{title}</div>
      <div className="text-[14px] text-venn-grey-80">{label}</div>
      <div className="formatting-editor group w-full grid gap-x-1 mt-1 rounded-[4px] border border-solid border-venn-grey-30 focus-within:border-venn-dark-blue [&_.formatting-editor-field_b]:text-venn-black">
        <div className="formatting-header flex justify-between items-center py-[8px] px-[12px] bg-venn-grey-10 rounded-t-[4px]">
          <div className="formatting-toolbar flex gap-2 justify-between">
            <FormatButton
              command="bold"
              label="Bold"
              icon="bold"
              isActive={activeFormats.bold}
              disabled={isReadOnly || !isFocused}
              onFormat={handleFormat}
            />
            <FormatButton
              command="italic"
              label="Italic"
              icon="italic"
              isActive={activeFormats.italic}
              disabled={isReadOnly || !isFocused}
              onFormat={handleFormat}
            />
            <FormatButton
              command="underline"
              label="Underline"
              icon="underline"
              isActive={activeFormats.underline}
              disabled={isReadOnly || !isFocused}
              onFormat={handleFormat}
            />
          </div>
          <div className="resetting-toolbar">
            <ResetButton disabled={isReadOnly} onClick={() => settingContentOnChange(undefined)} />
          </div>
        </div>
        {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
        <div
          ref={editorRef}
          contentEditable={!isReadOnly}
          suppressContentEditableWarning
          onInput={handleInput}
          onPaste={handlePaste}
          onKeyUp={handleKeyUp}
          onMouseUp={handleMouseUp}
          onFocus={() => {
            isFocusedRef.current = true;
            setIsFocused(true);
          }}
          onBlur={() => {
            isFocusedRef.current = false;
            setIsFocused(false);
          }}
          className={cn(
            'formatting-editor-field text-[13px] font-normal min-h-[100px] w-full overflow-y-auto py-[8px] px-[12px] outline-none ring-0',
            '[&_p]:mt-0 [&_p]:mb-[0.75em] [&_p:last-child]:mb-0',
            'focus:outline-none focus:ring-0 [&.focus-visible]:outline-none ',
            isReadOnly && 'bg-gray-50 text-venn-grey-80',
          )}
        />
      </div>
    </div>
  );
};
